import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../App';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';

export default function Login() {
  const { isNightMode } = useTheme();
  const navigate = useNavigate();
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
  const googleSignInButtonRef = useRef(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const googleRetryAttempted = useRef(false);

  // Load Google Identity Services script
  useEffect(() => {
    // Check if script is already loaded
    if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
  }, []);

  // Handle Google Sign-In button click
  const handleGoogleSignInClick = () => {
    const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    
    if (!GOOGLE_CLIENT_ID) {
      return;
    }

    if (window.google && window.google.accounts) {
      setIsGoogleLoading(true);
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'email profile',
        callback: (tokenResponse) => {
          if (tokenResponse.error) {
            console.error('Google Sign-In error:', tokenResponse.error);
            setIsGoogleLoading(false);
            return;
          }

          // Use the token to get user info
          fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          })
            .then((res) => {
              if (!res.ok) {
                throw new Error('Failed to fetch user info');
              }
              return res.json();
            })
            .then((userData) => {
              // Store user data in localStorage
              localStorage.setItem('user', JSON.stringify({
                email: userData.email,
                name: userData.name || 'User',
                picture: userData.picture,
                googleId: userData.id,
              }));
              
              // Dispatch custom event to notify App.js of auth change
              window.dispatchEvent(new Event('authChange'));
              
              // Redirect to home using react-router
              navigate('/');
            })
            .catch((error) => {
              console.error('Error fetching user info:', error);
              setIsGoogleLoading(false);
            });
        },
      });
      
      // Request access token (this will open the OAuth popup)
      tokenClient.requestAccessToken();
    } else {
      // Wait a bit for the script to load, then try again (only once)
      if (!googleRetryAttempted.current) {
        googleRetryAttempted.current = true;
        setTimeout(() => {
          if (window.google && window.google.accounts) {
            googleRetryAttempted.current = false;
            handleGoogleSignInClick();
          } else {
            setIsGoogleLoading(false);
            googleRetryAttempted.current = false;
          }
        }, 1000);
      } else {
        setIsGoogleLoading(false);
        googleRetryAttempted.current = false;
      }
    }
  };
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    // Validate only the selected login method
    if (loginMethod === 'email') {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
    } else if (loginMethod === 'phone') {
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
      }
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create user data payload
      const userData = {
        password: formData.password,
      };
      
      if (loginMethod === 'email' && formData.email.trim()) {
        userData.email = formData.email;
        userData.name = formData.email.split('@')[0];
      } else if (loginMethod === 'phone' && formData.phone.trim()) {
        userData.phone = formData.phone;
        userData.name = 'User ' + formData.phone.slice(-4);
      }

      // Try to login first
      let resData;
      try {
        resData = await apiClient.post('/auth/login', userData);
      } catch (loginError) {
        if (loginError.status === 400 && loginError.data && loginError.data.error === 'User not found') {
          try {
            // Provide a default name if they are automatically registering via login form
            const registerData = { ...userData, name: userData.name || 'Website User' };
            resData = await apiClient.post('/auth/register', registerData);
          } catch (registerError) {
            setErrors({ submit: registerError.message || 'Failed to authenticate' });
            return;
          }
        } else {
          setErrors({ submit: loginError.data?.error || loginError.message || 'Failed to authenticate' });
          return;
        }
      }

      if (resData) {
        localStorage.setItem('userAuth', 'true');
        localStorage.setItem('userData', JSON.stringify(resData.user || userData));
        localStorage.setItem('user', JSON.stringify(resData.user || userData));
        
        // Dispatch custom event for App.js (if not using SKIP_AUTH)
        window.dispatchEvent(new Event('authChange'));
        
        navigate('/');
      }
    } catch (err) {
      setErrors({ submit: err.message || 'Network error. Please make sure backend is running.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main
      style={{
        background: isNightMode ? '#020617' : '#f9fafb',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          width: '100%',
          maxWidth: '480px',
        }}
      >
        <motion.div
          style={{
            position: 'relative',
            background: isNightMode ? '#1e293b' : '#ffffff',
            borderRadius: '16px',
            padding: '40px',
            boxShadow: isNightMode
              ? '0 20px 60px rgba(0,0,0,0.4)'
              : '0 20px 60px rgba(0,0,0,0.1)',
            border: isNightMode ? '1px solid #334155' : '1px solid #e5e7eb',
          }}
          whileHover={{ scale: 1.01 }}
          transition={{ duration: 0.2 }}
        >
          {/* Back Button */}
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{
              position: 'absolute',
              top: '24px',
              left: '24px',
              background: 'transparent',
              border: 'none',
              color: isNightMode ? '#94a3b8' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '14px',
              fontWeight: 500,
              padding: '4px 8px',
              borderRadius: '6px',
              transition: 'background 0.2s',
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = isNightMode ? '#334155' : '#f3f4f6')}
            onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </button>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/Group.svg`}
                alt="Tiwari Syndicate"
                style={{
                  width: '60px',
                  height: '60px',
                  margin: '0 auto 16px',
                }}
              />
            </motion.div>
            <motion.h1
              style={{
                fontSize: '28px',
                fontWeight: 700,
                marginBottom: '8px',
                color: isNightMode ? '#f1f5f9' : '#111827',
              }}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Welcome Back
            </motion.h1>
            <motion.p
              style={{
                fontSize: '14px',
                color: isNightMode ? '#cbd5e1' : '#6b7280',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Log in to your Tiwari Syndicate account
            </motion.p>
          </div>

          {errors.submit && (
            <div style={{
              background: '#fee2e2',
              color: '#ef4444',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '24px',
              fontSize: '14px',
              fontWeight: 500,
              textAlign: 'center',
              border: '1px solid #fecaca'
            }}>
              {errors.submit}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <AnimatePresence mode="wait">
              <motion.div
                key="login"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Login Method Toggle */}
                <motion.div
                    style={{
                      display: 'flex',
                      background: isNightMode ? '#0f172a' : '#f3f4f6',
                      borderRadius: '10px',
                      padding: '4px',
                      marginBottom: '20px',
                    }}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.button
                      type="button"
                      onClick={() => {
                        setLoginMethod('email');
                        setErrors((prev) => ({ ...prev, email: '', phone: '' }));
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: loginMethod === 'email'
                          ? isNightMode
                            ? '#334155'
                            : '#ffffff'
                          : 'transparent',
                        color: loginMethod === 'email'
                          ? isNightMode
                            ? '#f1f5f9'
                            : '#111827'
                          : isNightMode
                          ? '#94a3b8'
                          : '#6b7280',
                        fontWeight: loginMethod === 'email' ? 600 : 400,
                        fontSize: '14px',
                        cursor: 'pointer',
                        boxShadow: loginMethod === 'email'
                          ? isNightMode
                            ? '0 2px 8px rgba(0,0,0,0.2)'
                            : '0 2px 8px rgba(0,0,0,0.1)'
                          : 'none',
                        transition: 'all 0.2s',
                      }}
                      whileHover={loginMethod !== 'email' ? { opacity: 0.8 } : {}}
                      whileTap={loginMethod !== 'email' ? { scale: 0.98 } : {}}
                    >
                      Email
                    </motion.button>
                    <motion.button
                      type="button"
                      onClick={() => {
                        setLoginMethod('phone');
                        setErrors((prev) => ({ ...prev, email: '', phone: '' }));
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: loginMethod === 'phone'
                          ? isNightMode
                            ? '#334155'
                            : '#ffffff'
                          : 'transparent',
                        color: loginMethod === 'phone'
                          ? isNightMode
                            ? '#f1f5f9'
                            : '#111827'
                          : isNightMode
                          ? '#94a3b8'
                          : '#6b7280',
                        fontWeight: loginMethod === 'phone' ? 600 : 400,
                        fontSize: '14px',
                        cursor: 'pointer',
                        boxShadow: loginMethod === 'phone'
                          ? isNightMode
                            ? '0 2px 8px rgba(0,0,0,0.2)'
                            : '0 2px 8px rgba(0,0,0,0.1)'
                          : 'none',
                        transition: 'all 0.2s',
                      }}
                      whileHover={loginMethod !== 'phone' ? { opacity: 0.8 } : {}}
                      whileTap={loginMethod !== 'phone' ? { scale: 0.98 } : {}}
                    >
                      Phone
                    </motion.button>
                  </motion.div>

                {/* Email Field */}
                <div style={{ marginBottom: '20px', display: loginMethod === 'phone' ? 'none' : 'block' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 500,
                      marginBottom: '8px',
                      color: isNightMode ? '#cbd5e1' : '#374151',
                    }}
                  >
                    Email Address {loginMethod === 'email' ? '*' : ''}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: errors.email
                        ? '2px solid #ef4444'
                        : isNightMode
                        ? '1px solid #334155'
                        : '1px solid #e5e7eb',
                      background: isNightMode ? '#0f172a' : '#f9fafb',
                      color: isNightMode ? '#f1f5f9' : '#111827',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#5d7f4e';
                      e.target.style.background = isNightMode ? '#1e293b' : '#ffffff';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.email
                        ? '#ef4444'
                        : isNightMode
                        ? '#334155'
                        : '#e5e7eb';
                      e.target.style.background = isNightMode ? '#0f172a' : '#f9fafb';
                    }}
                  />
                  {errors.email && (
                    <motion.p
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{
                        color: '#ef4444',
                        fontSize: '12px',
                        marginTop: '6px',
                      }}
                    >
                      {errors.email}
                    </motion.p>
                  )}
                </div>

                {/* Phone Field */}
                <div style={{ marginBottom: '20px', display: loginMethod === 'email' ? 'none' : 'block' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 500,
                      marginBottom: '8px',
                      color: isNightMode ? '#cbd5e1' : '#374151',
                    }}
                  >
                    Phone Number {loginMethod === 'phone' ? '*' : ''}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 1234567890"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: errors.phone
                        ? '2px solid #ef4444'
                        : isNightMode
                        ? '1px solid #334155'
                        : '1px solid #e5e7eb',
                      background: isNightMode ? '#0f172a' : '#f9fafb',
                      color: isNightMode ? '#f1f5f9' : '#111827',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#5d7f4e';
                      e.target.style.background = isNightMode ? '#1e293b' : '#ffffff';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.phone
                        ? '#ef4444'
                        : isNightMode
                        ? '#334155'
                        : '#e5e7eb';
                      e.target.style.background = isNightMode ? '#0f172a' : '#f9fafb';
                    }}
                  />
                  {errors.phone && (
                    <motion.p
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{
                        color: '#ef4444',
                        fontSize: '12px',
                        marginTop: '6px',
                      }}
                    >
                      {errors.phone}
                    </motion.p>
                  )}
                </div>

                {/* Password Field */}
                <div style={{ marginBottom: '20px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 500,
                      marginBottom: '8px',
                      color: isNightMode ? '#cbd5e1' : '#374151',
                    }}
                  >
                    Password *
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        paddingRight: '45px',
                        borderRadius: '10px',
                        border: errors.password
                          ? '2px solid #ef4444'
                          : isNightMode
                          ? '1px solid #334155'
                          : '1px solid #e5e7eb',
                        background: isNightMode ? '#0f172a' : '#f9fafb',
                        color: isNightMode ? '#f1f5f9' : '#111827',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'all 0.2s',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#5d7f4e';
                        e.target.style.background = isNightMode ? '#1e293b' : '#ffffff';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = errors.password
                          ? '#ef4444'
                          : isNightMode
                          ? '#334155'
                          : '#e5e7eb';
                        e.target.style.background = isNightMode ? '#0f172a' : '#f9fafb';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent',
                        border: 'none',
                        color: isNightMode ? '#94a3b8' : '#6b7280',
                        cursor: 'pointer',
                        fontSize: '18px',
                        padding: '4px',
                      }}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {errors.password && (
                    <motion.p
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{
                        color: '#ef4444',
                        fontSize: '12px',
                        marginTop: '6px',
                      }}
                    >
                      {errors.password}
                    </motion.p>
                  )}
                </div>

                {/* Forgot Password */}
                <motion.div
                    style={{
                      textAlign: 'right',
                      marginBottom: '24px',
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <a
                      href="#forgot-password"
                      style={{
                        color: '#5d7f4e',
                        fontSize: '13px',
                        textDecoration: 'none',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = 'none';
                      }}
                    >
                      Forgot password?
                    </a>
                  </motion.div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    borderRadius: '10px',
                    border: 'none',
                    background: isSubmitting
                      ? isNightMode
                        ? '#475569'
                        : '#9ca3af'
                      : '#5d7f4e',
                    color: '#ffffff',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    marginBottom: '20px',
                  }}
                  whileHover={!isSubmitting ? { scale: 1.02, y: -2 } : {}}
                  whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                >
                  {isSubmitting ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        style={{ display: 'inline-block' }}
                      >
                        ⏳
                      </motion.span>
                      Logging in...
                    </span>
                  ) : (
                    'Login'
                  )}
                </motion.button>

                {/* Divider */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '20px',
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      height: '1px',
                      background: isNightMode ? '#334155' : '#e5e7eb',
                    }}
                  />
                  <span
                    style={{
                      padding: '0 12px',
                      fontSize: '12px',
                      color: isNightMode ? '#94a3b8' : '#6b7280',
                    }}
                  >
                    OR
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: '1px',
                      background: isNightMode ? '#334155' : '#e5e7eb',
                    }}
                  />
                </div>

                {/* Social Login Buttons */}
                <div style={{ marginBottom: '20px' }}>
                  <motion.button
                    type="button"
                    onClick={handleGoogleSignInClick}
                    disabled={isGoogleLoading}
                    ref={googleSignInButtonRef}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '999px',
                      border: '2px solid #4285F4',
                      background: isGoogleLoading
                        ? isNightMode
                          ? '#475569'
                          : '#e5e7eb'
                        : '#ffffff',
                      color: isGoogleLoading
                        ? isNightMode
                          ? '#94a3b8'
                          : '#9ca3af'
                        : '#1f1f1f',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: isGoogleLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      fontFamily: 'Arial, sans-serif',
                      transition: 'all 0.2s ease',
                      opacity: isGoogleLoading ? 0.7 : 1,
                    }}
                    whileHover={!isGoogleLoading ? { 
                      scale: 1.01,
                      borderColor: '#357ae8',
                      boxShadow: '0 2px 8px rgba(66, 133, 244, 0.2)',
                    } : {}}
                    whileTap={!isGoogleLoading ? { scale: 0.99 } : {}}
                  >
                    {isGoogleLoading ? (
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        style={{ display: 'inline-block' }}
                      >
                        ⏳
                      </motion.span>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                    )}
                    <span>{isGoogleLoading ? 'Signing in...' : 'Sign in with Google'}</span>
                  </motion.button>
                </div>

              </motion.div>
            </AnimatePresence>
          </form>
        </motion.div>
      </motion.div>
    </main>
  );
}

